import { capitalize } from "lodash";
import { ScrollView, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from "react-native";
import { useSelector } from "react-redux";
import { fonts } from "src/fonts";
import { kDefaultProfileIconUri } from "src/util/constants";
import { kBrandEthosConversion } from "src/util/gFormatting";
import { kFonts } from "src/kFonts";
import { kColors } from "src/kColors";
import { getDefaultBrandProfile } from "src/redux/company/defaultData";
import { tModalComponentProps } from "src/redux/modals/types";
import { CSSProperties } from "react";
import { SLImage } from "src/components";
import { CancelIconNoBackground, ExternalLink } from "src/components/impComponents";
import { PdModal } from "src/components/overlay";
import { kStyleConst } from "src/kStyle";
import { getSafeUrl } from "src/util/functions";
import { openUrlInNewTab } from "src/util/linking";
import { tUseModal, useModal } from "src/providers/OverlayProvider";

const BrandReadMoreModal: React.FC<tModalComponentProps> = ({ onClose }) => {
  const rBrand = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData);

  const aSocials: Array<{ label: string; link: string }> = [];
  Object.entries(rBrand.links).forEach(([myKey, myValue]) => {
    if (myValue !== undefined) {
      let aLabel = capitalize(myKey);
      if (myKey === "linkedin") {
        aLabel = "LinkedIn";
      }

      aSocials.push({
        label: aLabel,
        link: myValue,
      });
    }
  });

  const kDefaultBreakPoint = 775;
  const kDefaultBreakPointLarge = 1200;
  const { width: sIsSmallDevice } = useWindowDimensions();

  const handleOnPressLink = (link?: string) => {
    openUrlInNewTab(getSafeUrl(link));
  };

  const handleOnClose = () => {
    onClose();
  };

  return (
    <PdModal
      containerStyle={
        sIsSmallDevice < kDefaultBreakPointLarge ? styles.P100_ModalContainerSmall : styles.P100_ModalContainer
      }
      style={sIsSmallDevice < kDefaultBreakPointLarge ? styles.P100_ModalSmall : styles.P100_Modal}
      onClose={handleOnClose}
    >
      <ScrollView showsHorizontalScrollIndicator={false}>
        <View style={styles.P100_ModelHeaderContainer}>
          {sIsSmallDevice < kDefaultBreakPoint ? (
            <>
              <View style={styles.P100_ModelHeaderBanner}>
                <SLImage
                  mobileStyle={sIsSmallDevice < kDefaultBreakPoint ? styles.P100_ModelIconSmall : styles.P100_ModelIcon}
                  webStyle={
                    sIsSmallDevice < kDefaultBreakPoint ? webStyles.P100_ModelIconSmall : webStyles.P100_ModelIcon
                  }
                  source={{ uri: rBrand.images.logoDownloadURL || getDefaultBrandProfile().images.logoDownloadURL }}
                  defaultSource={{ uri: kDefaultProfileIconUri }}
                />
                <View style={styles.P100_ModelNameAndLocationSmall}>
                  <Text style={fonts.AboutBrandHeaderSmallBrandName}>{rBrand.name}</Text>
                  <Text
                    style={[fonts.AboutBrandHeaderSmallLocation, styles.P100_Location]}
                  >{`${rBrand.addresses[0].city}, ${rBrand.addresses[0].state_or_province}`}</Text>
                </View>
                <TouchableOpacity style={styles.P100_ModelCancelIcon} onPress={handleOnClose}>
                  <CancelIconNoBackground />
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <>
              <View style={styles.P100_ModelHeaderBanner}>
                <TouchableOpacity style={styles.P100_ModelCancelIcon} onPress={handleOnClose}>
                  <CancelIconNoBackground />
                </TouchableOpacity>
              </View>
              <SLImage
                mobileStyle={sIsSmallDevice < kDefaultBreakPoint ? styles.P100_ModelIconSmall : styles.P100_ModelIcon}
                webStyle={
                  sIsSmallDevice < kDefaultBreakPoint ? webStyles.P100_ModelIconSmall : webStyles.P100_ModelIcon
                }
                source={{ uri: rBrand.images.logoDownloadURL || getDefaultBrandProfile().images.logoDownloadURL }}
                defaultSource={{ uri: kDefaultProfileIconUri }}
              />
            </>
          )}
        </View>
        {/* WHOLE INFO CONTAINER */}
        <View
          style={
            sIsSmallDevice < kDefaultBreakPoint ? styles.P100_ModelInfoContainerSmall : styles.P100_ModelInfoContainer
          }
        >
          {sIsSmallDevice < kDefaultBreakPoint ? (
            <>
              <View style={styles.P100_ModelInfoLeftContainerSmall}>
                {rBrand.isExclusive && (
                  <Text style={styles.fPoppinsExtraSmallWhiteBrandModelSmall}>MAIN TAB EXCLUSIVE</Text>
                )}
                <View style={styles.P100_ModelEthosAreaSmall}>
                  {rBrand.brandEthos.map((myEthos) => {
                    return (
                      <View style={styles.P100_EthosBox}>
                        <Text style={fonts.InformationText_White}>{kBrandEthosConversion[myEthos]}</Text>
                      </View>
                    );
                  })}
                </View>
                <View style={styles.P100_SocialsContainer}>
                  {aSocials.map(({ label: myLabel, link: myLink }, myIndex, myArray) => {
                    return (
                      <View>
                        <Text style={[fonts.InformationHeader_BrandDarkGreen, styles.P100_textTitle]}>
                          Social Media
                        </Text>
                        <View style={myIndex < myArray.length - 1 ? styles.P100_Social : {}}>
                          <TouchableOpacity onPress={() => handleOnPressLink(myLink)}>
                            <Text style={fonts.InformationText_BrandDark}>{myLabel}</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    );
                  })}
                </View>
                <Text style={[fonts.InformationHeader_BrandDarkGreen, styles.P100_textTitle]}>Website</Text>
                <TouchableOpacity onPress={() => handleOnPressLink(rBrand.website)} style={styles.gExternalLinkView}>
                  <Text style={fonts.InformationText_BrandDark}>Website</Text>
                  <View style={styles.gExternalLinkIcon}>
                    <ExternalLink />
                  </View>
                </TouchableOpacity>
              </View>
              <View style={styles.P100_ModelInfoRightContainerSmall}>
                <Text style={[fonts.AboutBrandHeaderText, styles.P100_AboutBrandHeaderText]}>ABOUT THIS BRAND</Text>
                <Text style={fonts.FacetCount_Black}>{rBrand.brandStory}</Text>
              </View>
            </>
          ) : (
            <>
              <View style={styles.P100_ModelInfoLeftContainer}>
                <View style={styles.P100_NameArea}>
                  <Text style={fonts.H1_Phone_BrandDark}>{rBrand.name}</Text>
                  {rBrand.isExclusive && (
                    <View style={styles.P100_ExclusiveIconView}>
                      <Text style={styles.P100_ExclusiveIcon}>MAIN TAB EXCLUSIVE</Text>
                    </View>
                  )}
                </View>
                <Text
                  style={[fonts.H4_BrandDark, styles.P100_Location]}
                >{`${rBrand.addresses[0].city}, ${rBrand.addresses[0].state_or_province}`}</Text>
                <View style={styles.P100_EthosArea}>
                  {rBrand.brandEthos.map((myEthos) => {
                    return (
                      <Text style={[fonts.InformationText_White, styles.P100_EthosBox]}>
                        {kBrandEthosConversion[myEthos]}
                      </Text>
                    );
                  })}
                </View>
                <View style={styles.P100_SocialsContainer}>
                  {aSocials.map(({ label: myLabel, link: myLink }, myIndex, myArray) => {
                    return (
                      <TouchableOpacity
                        onPress={() => handleOnPressLink(myLink)}
                        style={myIndex < myArray.length - 1 ? styles.P100_Social : {}}
                      >
                        <Text style={fonts.InformationText_BrandDark}>{myLabel}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
                <TouchableOpacity onPress={() => handleOnPressLink(rBrand.website)} style={styles.gExternalLinkView}>
                  <Text style={fonts.InformationText_BrandDark}>Website</Text>
                  <View style={styles.gExternalLinkIcon}>
                    <ExternalLink />
                  </View>
                </TouchableOpacity>
              </View>
              <View style={styles.P100_ModelInfoRightContainer}>
                <Text style={[fonts.AboutBrandHeaderText, styles.P100_AboutBrandHeaderText]}>ABOUT THIS BRAND</Text>
                <Text style={fonts.FacetCount_Black}>{rBrand.brandStory}</Text>
              </View>
            </>
          )}
        </View>
        {/* END WHOLE INFO CONTAINER */}
      </ScrollView>
    </PdModal>
  );
};

const useBrandReadMoreModal = (): tUseModal => {
  return useModal(BrandReadMoreModal);
};

export default useBrandReadMoreModal;

const styles = StyleSheet.create({
  P100_AboutBrandHeaderText: {
    alignSelf: "flex-start",
    borderTopWidth: 1,
    marginBottom: 5 * kStyleConst.kSpacing,
    width: kStyleConst.kReadStoryModelWidth,
  },
  P100_EthosArea: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  P100_EthosBox: {
    backgroundColor: kColors.brandDarkRed,
    marginBottom: 4 * kStyleConst.kSpacing,
    marginRight: 4 * kStyleConst.kSpacing,
    padding: 2 * kStyleConst.kSpacing,
  },
  P100_ExclusiveIcon: {
    ...kFonts.bodySmallCenter,
    alignSelf: "center",
    color: kColors.white,
    fontWeight: "400",
    letterSpacing: kStyleConst.kSlightLetterSpacing,
  },
  P100_ExclusiveIconView: {
    alignItems: "center",
    backgroundColor: kColors.brandGreen,
    borderRadius: 999,
    height: kStyleConst.kStoreFrontExclusiveBadgeHeight,
    justifyContent: "center",
    marginLeft: 4 * kStyleConst.kSpacing,
    paddingHorizontal: 2 * kStyleConst.kSpacing,
  },
  P100_Location: {
    marginBottom: 5 * kStyleConst.kSpacing,
    marginTop: kStyleConst.kSpacing,
    opacity: 0.9,
  },
  P100_Modal: {
    maxWidth: 1000,
  },
  P100_ModalContainer: { paddingHorizontal: 20 * kStyleConst.kSpacing },
  P100_ModalContainerSmall: { alignItems: undefined, justifyContent: undefined },
  P100_ModalSmall: {
    width: "100%",
  },
  P100_ModelCancelIcon: {
    position: "absolute",
    right: 10,
    top: 10,
  },
  P100_ModelEthosAreaSmall: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 4 * kStyleConst.kSpacing,
  },
  P100_ModelHeaderBanner: {
    backgroundColor: kColors.brandDarkGreen,
    flexDirection: "row",
    height: kStyleConst.kBrandBannerHeight,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    width: "100%",
  },
  P100_ModelHeaderContainer: {
    zIndex: 10,
  },
  P100_ModelIcon: {
    backgroundColor: kColors.white,
    borderRadius: 999,
    height: kStyleConst.kBrandIconDiameter,
    marginLeft: 24 * kStyleConst.kSpacing,
    marginTop: -30 * kStyleConst.kSpacing,
    width: kStyleConst.kBrandIconDiameter,
  },
  P100_ModelIconSmall: {
    alignSelf: "flex-start",
    backgroundColor: kColors.white,
    borderRadius: 999,
    height: kStyleConst.kBrandIconDiameterSmall,
    marginBottom: "auto",
    marginTop: "auto",
    width: kStyleConst.kBrandIconDiameterSmall,
  },
  P100_ModelInfoContainer: {
    flexDirection: "row",
    marginTop: 30 * kStyleConst.kSpacing - kStyleConst.kBrandIconDiameter,
  },
  P100_ModelInfoContainerSmall: {
    flexDirection: "column",
    marginTop: 30 * kStyleConst.kSpacing - kStyleConst.kBrandIconDiameter,
  },
  P100_ModelInfoLeftContainer: {
    alignContent: "flex-start",
    backgroundColor: kColors.grayPink,
    flex: 7,
    paddingBottom: 20 * kStyleConst.kSpacing,
    paddingLeft: 16 * kStyleConst.kSpacing,
    paddingRight: 12 * kStyleConst.kSpacing,
    paddingTop: 19 * kStyleConst.kSpacing,
  },
  P100_ModelInfoLeftContainerSmall: {
    alignContent: "flex-start",
    backgroundColor: kColors.grayPink,
    paddingBottom: 6 * kStyleConst.kSpacing,
    paddingLeft: 6 * kStyleConst.kSpacing,
    paddingRight: 16 * kStyleConst.kSpacing,
    paddingTop: 19 * kStyleConst.kSpacing,
  },
  P100_ModelInfoRightContainer: {
    backgroundColor: kColors.white,
    flex: 8,
    paddingHorizontal: 16 * kStyleConst.kSpacing,
    paddingVertical: 14 * kStyleConst.kSpacing,
  },
  P100_ModelInfoRightContainerSmall: {
    backgroundColor: kColors.white,
    flex: 8,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 10 * kStyleConst.kSpacing,
  },
  P100_ModelNameAndLocationSmall: {
    height: kStyleConst.kBrandIconDiameter,
    justifyContent: "center",
    marginBottom: "auto",
    marginLeft: 7 * kStyleConst.kSpacing,
    marginTop: "auto",
    width: kStyleConst.kBrandIconDiameter,
  },
  P100_NameArea: {
    alignItems: "center",
    flexDirection: "row",
  },
  P100_Social: {
    borderRightColor: kColors.black,
    borderRightWidth: 0.75,
    marginRight: 3 * kStyleConst.kSpacing,
    paddingRight: 3 * kStyleConst.kSpacing,
  },
  P100_SocialsContainer: {
    flexDirection: "row",
    marginBottom: 6 * kStyleConst.kSpacing,
  },

  P100_textTitle: {
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  fPoppinsExtraSmallWhiteBrandModelSmall: {
    ...kFonts.poppinsExtraSmall,
    alignSelf: "flex-start",
    backgroundColor: kColors.brandGreen,
    borderRadius: 999,
    color: kColors.white,
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    paddingVertical: kStyleConst.kSpacing,
  },
  gExternalLinkIcon: {
    ...kStyleConst.kExternalLinkIconDimensions,
    marginStart: kStyleConst.kSpacing,
    marginTop: 3,
  },
  gExternalLinkView: {
    flexDirection: "row",
  },
});

const webStyles: { [key: string]: CSSProperties } = {
  P100_BrandReadModalImg: {
    objectFit: "contain",
    maxHeight: "inherit",
    maxWidth: "inherit",
  },
  P100_ModelIconSmall: {
    alignSelf: "flex-start",
    borderRadius: 999,
    height: kStyleConst.kBrandIconDiameterSmall,
    marginBottom: "auto",
    marginTop: "auto",
    width: kStyleConst.kBrandIconDiameterSmall,
  },
  P100_ModelIcon: {
    backgroundColor: kColors.white,
    borderRadius: 999,
    height: kStyleConst.kBrandIconDiameter,
    marginLeft: 24 * kStyleConst.kSpacing,
    marginTop: -30 * kStyleConst.kSpacing,
    width: kStyleConst.kBrandIconDiameter,
  },
  P100_BrandReadModelImg1: {
    marginLeft: 20,
    marginRight: 300,
    maxHeight: 390,
    maxWidth: 590,
    minHeight: 86,
    minWidth: 183,
  },
  P100_BrandReadModelImg2: {
    marginRight: 20,
    marginLeft: 300,
    maxHeight: 400,
    maxWidth: 424,
    minHeight: 86,
    minWidth: 183,
  },
  P100_BrandReadModelImg3: {
    maxHeight: 451,
    maxWidth: 774,
    minHeight: 109,
    minWidth: 260,
  },
} as const;
